import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1300;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 500px;
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: modalFadeIn 0.2s ease-out;

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const IconWrapper = styled.div<{ isSuccess: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${props => props.isSuccess ? '#E8F5E9' : '#FFEBEE'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  svg {
    width: 28px;
    height: 28px;
    color: ${props => props.isSuccess ? '#2E7D32' : '#C62828'};
  }
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #042B41;
  margin: 0 0 12px 0;
  text-align: center;
`;

const ModalMessage = styled.p`
  font-size: 14px;
  color: #4B5563;
  text-align: center;
  line-height: 1.5;
  margin: 0 0 24px 0;
  white-space: pre-line;
`;

const ErrorListContainer = styled.div`
  width: 100%;
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  
  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #F1F1F1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #CBD5E1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94A3B8;
  }
`;

const ErrorItem = styled.div`
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  color: #C62828;
  padding: 6px 10px;
  background-color: #FFF5F5;
  border-left: 3px solid #E53935;
  border-radius: 4px;
  word-break: break-word;
`;

const Button = styled.button`
  background-color: #042B41;
  color: white;
  border: 1px solid #042B41;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
  text-align: center;
  
  &:hover {
    background-color: #03223A;
    border-color: #03223A;
  }
`;

interface ImportResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  successCount: number;
  errorCount: number;
  errorDetails: string[];
  supplierName: string;
}

export const ImportResultModal: React.FC<ImportResultModalProps> = ({
  isOpen,
  onClose,
  successCount,
  errorCount,
  errorDetails,
  supplierName,
}) => {
  if (!isOpen) return null;

  const isSuccess = errorCount === 0;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <IconWrapper isSuccess={isSuccess}>
          {isSuccess ? (
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          )}
        </IconWrapper>

        <ModalTitle>
          {isSuccess ? 'Import Completed Successfully' : 'Import Completed with Errors'}
        </ModalTitle>

        <ModalMessage>
          {isSuccess
            ? `Supplier "${supplierName}" created successfully!\n\nSuccessfully imported all ${successCount} inventory items.`
            : `Supplier "${supplierName}" created successfully, but some inventory items failed to import.\n\nSuccessfully imported: ${successCount} item(s)\nFailed to import: ${errorCount} item(s)`}
        </ModalMessage>

        {!isSuccess && errorDetails.length > 0 && (
          <ErrorListContainer>
            {errorDetails.map((error, index) => (
              <ErrorItem key={index}>{error}</ErrorItem>
            ))}
          </ErrorListContainer>
        )}

        <Button onClick={onClose}>Dismiss</Button>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ImportResultModal;
